import { auth } from "firebase/app";
import { SnackbarModule } from "@/store/modules/SnackbarModule";

/**
 * The IRES Admin Client's read cache (2026-11-02): the last answer of a read, shown at once the next time
 * the same read is made, while IRES Cloud is asked again. Every IRES Admin Client read goes through a
 * callable that takes seconds (the client list 2.7 s typical, 6.8 s for one call in ten), so a screen
 * opened a second time no longer waits on a spinner for data it showed a moment ago.
 *
 * The rules that keep it from showing stale data as current:
 * - **Cached data is never the last word.** A screen that shows a cached answer always asks IRES Cloud
 *   too and shows that answer when it arrives ({@link readThroughCache}); a screen disables its edit
 *   actions until then, so no change starts from an old answer.
 * - **Per tab, in memory only.** Nothing goes into browser storage, so nothing outlives the tab, and an
 *   "Als Mandant anmelden" tab (another user) never sees it.
 * - **Per signed-in user**, and emptied on every sign-in, sign-out and client switch
 *   (`clearCacheAndModules`).
 * - **Bounded**: the {@link MAX_ANSWERS_PER_READ} most recently used answers per read.
 *
 * Answers are kept as JSON text and parsed on every recall, so a screen that changes the rows it was
 * given never changes the cached answer. The callables answer plain JSON, so nothing is lost.
 */

/** How many answers one read keeps (e.g. the detail of the last 20 clients opened). */
export const MAX_ANSWERS_PER_READ = 20;

/** Per read, its answers by argument key; a Map keeps insertion order, oldest first. */
const answers = new Map<string, Map<string, string>>();

/** The user the answers were read for; another user empties the cache. */
let ownerUid: string | null = null;

function currentUid(): string | null {
  return auth().currentUser?.uid ?? null;
}

/** The answers of one read, emptied first when they belong to another user than the one signed in. */
function answersOf(read: string): Map<string, string> {
  const uid = currentUid();
  if (uid !== ownerUid) {
    answers.clear();
    ownerUid = uid;
  }

  let ofRead = answers.get(read);
  if (!ofRead) {
    ofRead = new Map();
    answers.set(read, ofRead);
  }
  return ofRead;
}

function argumentKey(args: unknown): string {
  return JSON.stringify(args ?? null);
}

/**
 * Keeps IRES Cloud's answer to a read, as the most recent one. Called by the repository after every
 * successful read, so a re-read after a save leaves the post-save answer.
 *
 * @param read - the callable's name.
 * @param args - what the answer depends on (a client id, a filter); `null` for none.
 * @param answer - the answer, plain JSON data.
 */
export function rememberAdminRead(read: string, args: unknown, answer: unknown): void {
  if (currentUid() === null) {
    return;
  }
  const ofRead = answersOf(read);
  const key = argumentKey(args);
  ofRead.delete(key);
  ofRead.set(key, JSON.stringify(answer));

  while (ofRead.size > MAX_ANSWERS_PER_READ) {
    const oldest = ofRead.keys().next().value as string;
    ofRead.delete(oldest);
  }
}

/**
 * The last answer to a read, a fresh copy each time, or `null` when there is none for the signed-in user.
 *
 * @param read - the callable's name.
 * @param args - as given to {@link rememberAdminRead}.
 */
export function recallAdminRead<T>(read: string, args: unknown): T | null {
  if (currentUid() === null) {
    return null;
  }
  const ofRead = answersOf(read);
  const key = argumentKey(args);
  const text = ofRead.get(key);
  if (text === undefined) {
    return null;
  }
  // Most recently used last, so the bound drops what was not looked at for longest.
  ofRead.delete(key);
  ofRead.set(key, text);
  return JSON.parse(text) as T;
}

/**
 * Drops one answer, for a change elsewhere whose next read must not show the old one first (a group
 * save that moved a client in or out of the group).
 */
export function forgetAdminRead(read: string, args: unknown): void {
  answers.get(read)?.delete(argumentKey(args));
}

/** Empties the cache: on every sign-in, sign-out and client switch (`clearCacheAndModules`). */
export function clearAdminReadCache(): void {
  answers.clear();
  ownerUid = null;
}

/**
 * One read with the cache: shows the last answer at once when there is one, then asks IRES Cloud and
 * shows its answer.
 *
 * - Without a cached answer, a failure rejects as the read itself does; the screen shows its failure
 *   state as before.
 * - With a cached answer, a failure keeps it on screen and says so in a snackbar
 *   (`admin.refreshFailed`); the screen's edit actions stay disabled, and Aktualisieren retries
 *   (Taimoor, 2026-11-02: the safe option; 0 failures in about 960 such reads over four weeks).
 *
 * @param cached - the cached answer (the repository's `cached…` function), or `null`.
 * @param fetch - the read itself.
 * @param show - puts an answer on screen; `confirmed` is false for the cached one, true for IRES Cloud's.
 * @returns whether IRES Cloud's answer is on screen.
 */
export async function readThroughCache<T>(
  cached: T | null,
  fetch: () => Promise<T>,
  show: (answer: T, confirmed: boolean) => void
): Promise<boolean> {
  if (cached !== null) {
    show(cached, false);
  }

  try {
    show(await fetch(), true);
    return true;
  } catch (error) {
    if (cached === null) {
      throw error;
    }
    SnackbarModule.showError({ key: "admin.refreshFailed" });
    return false;
  }
}
